import React from "react";

const APP_ELFSIGHT = "elfsight-app-d1095e31-2a31-47b0-9022-67301b69e2cf";
const PLATEFORME = "https://static.elfsight.com/platform/platform.js";

/**
 * Widget d'avis Google (Elfsight), chargé seulement quand il approche de l'écran.
 *
 * Il était jusqu'ici monté dans le Layout avec un <Script strategy="idle">, donc
 * sur toutes les pages et systématiquement : 528 Ko de JavaScript, dont environ
 * la moitié jamais exécutée, pour un bloc situé tout en bas de page que la
 * plupart des visiteurs n'atteignent pas. C'était le premier poste de poids du
 * site, devant le SDK de consentement et la mesure d'audience réunis.
 *
 * L'attribut `data-elfsight-app-lazy` ne diffère que le rendu du widget : le
 * script, lui, était déjà téléchargé. D'où l'IntersectionObserver ci-dessous,
 * qui déclenche l'injection 400 px avant que le bloc n'entre dans le champ.
 *
 * À noter pour le RGPD : Elfsight ne dépose aucun cookie ici (vérifié au moment
 * de la reprise), mais son CDN voit passer l'adresse IP du visiteur. Le report
 * du chargement fait qu'il ne la voit plus que si l'on descend jusqu'aux avis.
 */
export default function AvisGoogle() {
  const conteneur = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const cible = conteneur.current;
    if (!cible) return;

    let annule = false;

    const charger = () => {
      if (annule) return;
      if (document.querySelector(`script[src="${PLATEFORME}"]`)) return;
      const script = document.createElement("script");
      script.src = PLATEFORME;
      script.async = true;
      script.setAttribute("data-use-service-core", "");
      document.body.appendChild(script);
    };

    // Sans IntersectionObserver (navigateurs anciens), on charge sans condition :
    // mieux vaut le poids que des avis absents.
    if (typeof IntersectionObserver === "undefined") {
      charger();
      return;
    }

    const observateur = new IntersectionObserver(
      entrees => {
        if (entrees.some(entree => entree.isIntersecting)) {
          observateur.disconnect();
          charger();
        }
      },
      { rootMargin: "400px" }
    );

    observateur.observe(cible);

    return () => {
      annule = true;
      observateur.disconnect();
    };
  }, []);

  return <div ref={conteneur} className={APP_ELFSIGHT} data-elfsight-app-lazy />;
}
